import { useState, useMemo } from 'react'
import { useBigOpportunities } from '@/hooks/use-api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ReportFilters } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Download, Filter, DollarSign, TrendingUp, Trophy, Target, Loader2 } from 'lucide-react'
import { formatCurrency, formatDate } from '@/lib/utils'
import {
  CHART_COLORS,
  ROTATED_LABEL_CHART_MARGIN,
  formatChartCurrency,
  CATEGORICAL,
  SEMANTIC,
} from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { explainError } from '@/lib/explain-error'

const COLORS = CHART_COLORS

export default function BigOpportunities() {
  const [filters, setFilters] = useState<ReportFilterValues>({})
  const [limit, setLimit] = useState('25')
  const [minAmount, setMinAmount] = useState('100000')
  const [status, setStatus] = useState('all')
  const [appliedFilters, setAppliedFilters] = useState<{
    limit?: number
    minAmount?: number
    status?: string
  }>({
    limit: 25,
    minAmount: 100000,
    status: 'all',
  })

  const { data, isLoading, error } = useBigOpportunities({
    ...appliedFilters,
    ...(filters.owner_id ? { owner_id: filters.owner_id } : {}),
  })

  // Transform by-stage data for bar chart
  const byStageChartData = useMemo(() => {
    if (!data?.byStage) return []
    return data.byStage.map((item: any) => ({
      stage: item.stage_name,
      totalValue: parseFloat(item.total_value || '0'),
      count: item.opportunity_count,
      avgAmount: parseFloat(item.avg_amount || '0'),
    }))
  }, [data])

  // Transform by-industry data for bar chart
  const byIndustryChartData = useMemo(() => {
    if (!data?.byIndustry) return []
    return data.byIndustry.map((item: any) => ({
      industry: item.industry || 'Unknown',
      totalValue: parseFloat(item.total_value || '0'),
      count: item.opportunity_count,
    }))
  }, [data])

  const summary = data?.summary

  const handleApplyFilters = () => {
    setAppliedFilters({
      limit: parseInt(limit),
      minAmount: parseInt(minAmount),
      status,
    })
  }

  const handleClearFilters = () => {
    setLimit('25')
    setMinAmount('100000')
    setStatus('all')
    setAppliedFilters({ limit: 25, minAmount: 100000, status: 'all' })
  }

  const handleExportCSV = () => {
    if (!data?.opportunities) return

    const exportData = data.opportunities.map((item: any) => ({
      'Opportunity Name': item.name,
      Account: item.account_name,
      'Amount (CAD)': item.amount_cad,
      'Source Currency': item.currency_iso_code,
      Stage: item.stage_name,
      Probability: `${item.probability}%`,
      'Close Date': item.close_date,
      Owner: item.owner_name,
      Status: item.stage_name === 'Closed Won' ? 'Won' : item.is_closed ? 'Lost' : 'Open',
    }))

    exportToCSV({ data: exportData, filename: 'big-opportunities.csv' })
  }

  // Check if we have data but it's empty
  const hasEmptyResults =
    !isLoading && !error && data && (!data.opportunities || data.opportunities.length === 0)

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Big Opportunities</h1>
          <p className="text-muted-foreground">Track high-value deals in the pipeline</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-destructive font-medium">Failed to load big opportunities data.</p>
              <p className="text-sm text-muted-foreground">
                Error: {explainError(error, 'Unknown error occurred')}
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                aria-label="Reload page"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Big Opportunities</h1>
          <p className="text-muted-foreground">Track high-value deals in the pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <ReportFilters
            value={filters}
            onChange={setFilters}
            defaultPeriod="all"
            showPeriod={false}
            datePresets={[
              'this_month',
              'this_quarter',
              'fiscal_year',
              'calendar_year',
              'last_12_months',
              'all',
              'custom',
            ]}
          />
          <Button
            onClick={handleExportCSV}
            variant="outline"
            disabled={isLoading || !data?.opportunities}
            aria-label="Export big opportunities to CSV"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" aria-hidden="true" />
            Filters
          </CardTitle>
          <CardDescription>Configure opportunity filtering criteria</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <Label htmlFor="minAmount">Minimum Amount ($)</Label>
              <Input
                id="minAmount"
                type="number"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                placeholder="100000"
                aria-label="Minimum opportunity amount"
              />
            </div>
            <div>
              <Label htmlFor="status">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="status" aria-label="Select opportunity status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Opportunities</SelectItem>
                  <SelectItem value="open">Open Only</SelectItem>
                  <SelectItem value="closed_won">Closed Won</SelectItem>
                  <SelectItem value="closed_lost">Closed Lost</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="limit">Results Limit</Label>
              <Select value={limit} onValueChange={setLimit}>
                <SelectTrigger id="limit" aria-label="Select number of results">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">Top 10</SelectItem>
                  <SelectItem value="25">Top 25</SelectItem>
                  <SelectItem value="50">Top 50</SelectItem>
                  <SelectItem value="100">Top 100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleApplyFilters} className="flex-1">
                Apply Filters
              </Button>
              <Button onClick={handleClearFilters} variant="outline">
                Clear
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Empty State Warning */}
      {hasEmptyResults && (
        <Card
          style={{
            borderColor: SEMANTIC.warning.fill,
            backgroundColor: SEMANTIC.warning.fill,
          }}
        >
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
                No opportunities found matching your criteria
              </p>
              <p className="text-sm" style={{ color: SEMANTIC.warning.text }}>
                Try lowering the minimum amount or changing the status filter to see more results.
              </p>
              <Button
                onClick={handleClearFilters}
                variant="outline"
                size="sm"
                className="mt-2"
                aria-label="Clear filters"
              >
                Clear Filters
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            aria-label="Loading big opportunities data"
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Total Value (CAD)</CardTitle>
                <DollarSign
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.evergreen.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(summary?.total_value || 0, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  combined amount
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Avg Deal Size (CAD)</CardTitle>
                <TrendingUp
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.navy.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(summary?.avg_amount || 0, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">average value</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Largest Deal (CAD)</CardTitle>
                <Trophy
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.plum.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(summary?.max_amount || 0, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">highest value</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Count</CardTitle>
                <Target
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.clay.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {summary?.opportunity_count?.toLocaleString() || '0'}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">opportunities</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* By Stage Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Value by Stage</CardTitle>
                <CardDescription>Big opportunity distribution across stages (CAD)</CardDescription>
              </CardHeader>
              <CardContent>
                {byStageChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No stage data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={byStageChartData} margin={ROTATED_LABEL_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="stage"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tickFormatter={formatChartCurrency} tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number, name: string) => {
                          if (name === 'Total Value' || name === 'Avg Amount')
                            return formatCurrency(value, 'CAD')
                          return value
                        }}
                      />
                      <Legend />
                      <Bar dataKey="totalValue" fill={COLORS[0]} name="Total Value" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* By Industry Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Value by Industry</CardTitle>
                <CardDescription>
                  Big opportunities across different industries (CAD)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {byIndustryChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No industry data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={byIndustryChartData} margin={ROTATED_LABEL_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="industry"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tickFormatter={formatChartCurrency} tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number, name: string) => {
                          if (name === 'Total Value') return formatCurrency(value, 'CAD')
                          return value
                        }}
                      />
                      <Legend />
                      <Bar dataKey="totalValue" fill={COLORS[1]} name="Total Value" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>Big Opportunity Details</CardTitle>
              <CardDescription>Complete list of high-value opportunities</CardDescription>
            </CardHeader>
            <CardContent>
              {!data?.opportunities || data.opportunities.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  No opportunities available
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm"
                    role="table"
                    aria-label="Big opportunities detailed list"
                  >
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">Opportunity</th>
                        <th className="text-left p-2 font-medium">Account</th>
                        <th className="text-right p-2 font-medium">Amount (CAD)</th>
                        <th className="text-left p-2 font-medium">Source Currency</th>
                        <th className="text-left p-2 font-medium">Stage</th>
                        <th className="text-right p-2 font-medium">Probability</th>
                        <th className="text-left p-2 font-medium">Close Date</th>
                        <th className="text-left p-2 font-medium">Owner</th>
                        <th className="text-left p-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.opportunities.map((item: any, index: number) => (
                        <tr key={index} className="border-b hover:bg-muted/50">
                          <td className="p-2">{item.opportunity_name}</td>
                          <td className="p-2">{item.account_name}</td>
                          <td className="text-right p-2">
                            {formatCurrency(parseFloat(item.amount_cad || '0'), 'CAD')}
                          </td>
                          <td className="p-2">{item.currency_iso_code || '-'}</td>
                          <td className="p-2">{item.stage_name}</td>
                          <td className="text-right p-2">{item.probability}%</td>
                          <td className="p-2">{formatDate(item.close_date)}</td>
                          <td className="p-2">{item.owner_name}</td>
                          <td className="p-2">
                            <span
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                              style={{
                                backgroundColor:
                                  item.stage_name === 'Closed Won'
                                    ? SEMANTIC.success.fill
                                    : item.is_closed
                                      ? SEMANTIC.error.fill
                                      : SEMANTIC.info.fill,
                                color:
                                  item.stage_name === 'Closed Won'
                                    ? SEMANTIC.success.text
                                    : item.is_closed
                                      ? SEMANTIC.error.text
                                      : SEMANTIC.info.text,
                              }}
                            >
                              {item.stage_name === 'Closed Won'
                                ? 'Won'
                                : item.is_closed
                                  ? 'Lost'
                                  : 'Open'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
